import type {
  AppSettings,
  BetaFeedback,
  CrashLog,
  DashboardSnapshot,
  Incident,
  IntakeParseResult,
  Job,
  LegacyBrowserSnapshot,
  LibraryCard
} from '@shared/domain';
import { FLOW_DEFINITIONS, type FlowDefinition } from '@shared/flows';
import {
  buildJobTextSummary,
  clearBetaFeedbackLocal,
  clearCrashLogsLocal,
  clearLocalData,
  deleteIncidentLocal,
  deleteJobLocal,
  deleteLibraryCardLocal,
  exportAllDataLocal,
  getDailyTrainingCardLocal,
  getDashboardSnapshotLocal,
  getHealthLocal,
  getJobLocal,
  getPhotoStorageSummaryLocal,
  getSettingsLocal,
  getStorageUsageLocal,
  listBetaFeedbackLocal,
  listCrashLogsLocal,
  getWeeklyTrainingProgressLocal,
  importLegacySnapshotLocal,
  listIncidentsLocal,
  listJobsLocal,
  listLibraryCardsLocal,
  parseIntakeLocal,
  recordTrainingReview,
  saveBetaFeedbackLocal,
  saveCrashLogLocal,
  saveIncidentLocal,
  saveJobLocal,
  saveLibraryCardLocal,
  saveSettingsLocal
} from './localData';

const API_BASE = 'local://device';

export function getApiBase(): string {
  return API_BASE;
}

export async function fetchHealth(): Promise<{ ok: boolean; version: string; now: string }> {
  return getHealthLocal();
}

export async function fetchFlows(): Promise<FlowDefinition[]> {
  return FLOW_DEFINITIONS;
}

export async function fetchDashboard(): Promise<DashboardSnapshot> {
  return getDashboardSnapshotLocal();
}

export async function fetchJobs(filters: { query?: string; status?: string } = {}): Promise<Job[]> {
  return listJobsLocal({
    ...(filters.query ? { query: filters.query } : {}),
    ...(filters.status ? { status: filters.status as Job['status'] | 'All' } : {})
  });
}

export async function fetchJob(id: string): Promise<Job> {
  const job = getJobLocal(id);
  if (!job) {
    throw new Error(`Job ${id} not found`);
  }
  return job;
}

export async function createJob(job: Job): Promise<{ data: Job; queued: boolean }> {
  return { data: saveJobLocal(job), queued: false };
}

export async function saveJob(job: Job): Promise<{ data: Job; queued: boolean }> {
  return { data: saveJobLocal(job), queued: false };
}

export async function destroyJob(job: Job): Promise<{ queued: boolean }> {
  deleteJobLocal(job.id);
  return { queued: false };
}

export async function fetchLibraryCards(filters: { query?: string; tag?: string; favorite?: boolean } = {}): Promise<LibraryCard[]> {
  return listLibraryCardsLocal(filters);
}

export async function saveLibraryCard(card: LibraryCard, _isNew: boolean): Promise<{ data: LibraryCard; queued: boolean }> {
  return { data: saveLibraryCardLocal(card), queued: false };
}

export async function destroyLibraryCard(card: LibraryCard): Promise<{ queued: boolean }> {
  deleteLibraryCardLocal(card.id);
  return { queued: false };
}

export async function parseIntake(text: string): Promise<IntakeParseResult> {
  return parseIntakeLocal(text);
}

export async function fetchExportBundle(): Promise<unknown> {
  return exportAllDataLocal();
}

export async function resetServerData(): Promise<void> {
  clearLocalData();
}

export async function importLegacyBrowserData(snapshot: LegacyBrowserSnapshot): Promise<{ jobsImported: number; cardsImported: number }> {
  return importLegacySnapshotLocal(snapshot);
}

export async function fetchSettings(): Promise<AppSettings> {
  return getSettingsLocal();
}

export async function saveSettings(nextSettings: Partial<AppSettings>): Promise<AppSettings> {
  return saveSettingsLocal(nextSettings);
}

export async function fetchIncidents(filters: {
  query?: string;
  type?: Incident['type'] | 'All';
  unresolvedOnly?: boolean;
} = {}): Promise<Incident[]> {
  return listIncidentsLocal(filters);
}

export async function saveIncident(incident: Incident): Promise<Incident> {
  return saveIncidentLocal(incident);
}

export async function destroyIncident(incidentId: string): Promise<void> {
  deleteIncidentLocal(incidentId);
}

export async function fetchDailyTrainingCard(): Promise<LibraryCard | null> {
  return getDailyTrainingCardLocal();
}

export async function markTrainingCardReviewed(cardId: string): Promise<AppSettings> {
  return recordTrainingReview(cardId);
}

export async function fetchTrainingProgress(): Promise<{ reviewed: number; total: number }> {
  return getWeeklyTrainingProgressLocal();
}

export async function fetchStorageQuota(): Promise<{
  usedBytes: number;
  limitBytes: number;
  percent: number;
  nearingLimit: boolean;
}> {
  return getStorageUsageLocal();
}

export async function fetchPhotoStorageSummary(): Promise<{ count: number; bytes: number }> {
  return getPhotoStorageSummaryLocal();
}

export async function buildJobExportSummary(job: Job): Promise<string> {
  return buildJobTextSummary(job);
}

export async function fetchBetaFeedback(): Promise<BetaFeedback[]> {
  return listBetaFeedbackLocal();
}

export async function saveBetaFeedback(feedback: BetaFeedback): Promise<BetaFeedback> {
  return saveBetaFeedbackLocal(feedback);
}

export async function clearBetaFeedback(): Promise<void> {
  clearBetaFeedbackLocal();
}

export async function fetchCrashLogs(): Promise<CrashLog[]> {
  return listCrashLogsLocal();
}

export async function saveCrashLog(log: CrashLog): Promise<CrashLog> {
  return saveCrashLogLocal(log);
}

export async function clearCrashLogs(): Promise<void> {
  clearCrashLogsLocal();
}
